import React, { useState, useEffect, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup from '../../component/BottomButtonGroup'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import { formatNumber } from '../../utils/numberDisplay'
import {
    applyAppGifticonCompanyOrderRefundDraft,
    clearAppGifticonCompanyOrderRefundDraft,
    createAppGifticonCompanyOrderRefundDraftPayload,
    createEditAppGifticonCompanyOrderRefundDraftKey,
    readAppGifticonCompanyOrderRefundDraft,
    saveAppGifticonCompanyOrderRefundDraft,
} from './appGifticonCompanyOrderRefundDraftUtils'

// 상세 조회 실패 시에도 폼 기본값을 명확하게 되돌릴 수 있게 공통 초기값을 사용한다.
const createDefaultParams = () => ({
    orderItemRefundId: '',
    refundQuantity: '',
    refundAmount: '',
    refundBank: '',
    refundAccount: '',
    refundReason: '',
    refundImage: '',
    refundTime: '',
    status: '',
    regTime: '',
    gifticon_order_item: {},
})

const REFUND_IMAGE_PANEL_SX = {
    p: 2,
    borderRadius: '4px',
    borderColor: 'rgba(148, 163, 184, 0.24)',
    backgroundColor: '#fff',
}

const REFUND_IMAGE_PREVIEW_SX = {
    minHeight: 260,
    maxHeight: 520,
    borderRadius: '4px',
    border: '1px solid rgba(148, 163, 184, 0.24)',
    backgroundColor: '#f8fafc',
    overflow: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}

// 환불 증빙 이미지를 고정 카드 대신 화면 폭에 맞는 확인 패널로 보여준다.
function RefundImagePreview({ imageSrc }) {
    return (
        <Paper variant="outlined" sx={REFUND_IMAGE_PANEL_SX}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    환불 증빙 이미지
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    기업이 제출한 환불 증빙 이미지를 확인합니다.
                </Typography>
            </Box>
            <Box sx={REFUND_IMAGE_PREVIEW_SX}>
                <Box
                    component="img"
                    src={imageSrc}
                    alt="환불 증빙 이미지"
                    sx={{
                        width: '100%',
                        maxHeight: 520,
                        objectFit: 'contain',
                        display: 'block',
                    }}
                />
            </Box>
        </Paper>
    )
}

export default function AppGifticonCompanyOrderRefund(props) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.orderItemRefundId
    const curPage = locationState.curPage || 1
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || { searchStatus: '' }
    const listSearch = location.search || ''
    const draftKey = createEditAppGifticonCompanyOrderRefundDraftKey(id)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [draftReady, setDraftReady] = useState(false)
    const [params, setParams] = useState(() => createDefaultParams())
    const [mainImage, setMainImage] = useState('')
    const [openSnackbar, setOpenSnackbar] = useState(locationState.openSnackbar || false)
    const [severitySnackbar, setSeveritySnackbar] = useState(locationState.severitySnackbar || 'info')
    const [messageSnackbar, setMessageSnackbar] = useState(locationState.messageSnackbar || '')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const initialDraftSnapshotRef = useRef('')

    useEffect(() => {
        const getData = async () => {
            if (!id) {
                const errorMessage = '필수 파라미터가 없어 환불 요청 상세를 불러올 수 없습니다.'
                setParams(createDefaultParams())
                setMainImage('')
                setDraftReady(false)
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
                setLoading(false)
                return
            }

            setLoading(true)
            setDraftReady(false)
            setDetailErrorMessage('')

            try {
                const { data } = await API.post('/api/v1/admin/gifticonOrderRefund/getDetail', { orderItemRefundId: id })
                if (data.code === 200) {
                    const baseParams = data.data || {}

                    if (!baseParams.orderItemRefundId) {
                        setParams(createDefaultParams())
                        setMainImage('')
                        setDetailErrorMessage('환불 요청 상세 정보를 찾을 수 없습니다.')
                        setLoading(false)
                        return
                    }

                    const storedDraft = readAppGifticonCompanyOrderRefundDraft(draftKey)
                    const restoredDraft = applyAppGifticonCompanyOrderRefundDraft({
                        baseParams,
                        draft: storedDraft,
                    })

                    initialDraftSnapshotRef.current = JSON.stringify(createAppGifticonCompanyOrderRefundDraftPayload({
                        params: baseParams,
                    }))

                    setParams(restoredDraft.params)
                    setMainImage(baseParams.refundImage === null ? '/images/noimage.jpg' : `${process.env.REACT_APP_AWS_S3_URL}/${baseParams.refundImage}`)
                    setDetailErrorMessage('')
                    setDraftReady(true)
                } else if (data.code === 403) {
                    setLoading(false)
                    history.push('/login')
                    return
                } else {
                    const errorMessage = data.message || '환불 요청 상세를 불러오지 못했습니다.'
                    setParams(createDefaultParams())
                    setMainImage('')
                    setDetailErrorMessage(errorMessage)
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(errorMessage)
                }
            } catch (_error) {
                const errorMessage = '환불 요청 상세를 불러오지 못했습니다.'
                setParams(createDefaultParams())
                setMainImage('')
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }

            setLoading(false)
        }
        getData()
    }, [draftKey, history, id])

    useEffect(() => {
        if (!draftReady || !params?.orderItemRefundId) {
            return
        }

        // 환불 요청 상세는 목록 query를 건드리지 않고 상태 draft만 sessionStorage에 보존한다.
        const nextDraft = createAppGifticonCompanyOrderRefundDraftPayload({
            params,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearAppGifticonCompanyOrderRefundDraft(draftKey)
            return
        }

        saveAppGifticonCompanyOrderRefundDraft(draftKey, nextDraft)
    }, [draftKey, draftReady, params])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    // 상세 저장 후에도 원래 목록 query와 필터를 그대로 유지한 채 복귀한다.
    const moveToList = (snackbarState) => {
        clearAppGifticonCompanyOrderRefundDraft(draftKey)
        history.push(createListLinkTarget({
            pathname: '/appGifticonCompanyOrderRefunds',
            search: listSearch,
            searchText: searchText,
            searchSelect: searchSelect,
            curPage: curPage,
            ...(snackbarState ? {
                openSnackbar: true,
                severitySnackbar: snackbarState.severity,
                messageSnackbar: snackbarState.message,
            } : {})
        }))
    }

    const handleUpdateField = (e) => {
        setParams({
            ...params,
            [e.target.name]: e.target.value
        })
    }

    const updateData = async () => {
        if (saving) {
            return
        }

        try {
            setSaving(true)

            const { data } = await API.post('/api/v1/admin/gifticonOrderRefund/update', { orderItemRefundId: params.orderItemRefundId, status: params.status })
            if (data.code === 200) {
                moveToList({ severity: 'success', message: '환불 요청 상태를 저장했습니다.' })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('환불 요청 상태를 저장하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        updateData()
    }

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="환불 요청 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" path="/appGifticonCompanyOrderRefunds" visible={false} search={listSearch} searchText={searchText} searchSelect={searchSelect} curPage={curPage} onBackClick={() => clearAppGifticonCompanyOrderRefundDraft(draftKey)} backLabel="목록으로"/>
            )}
            sx={{ mt: 2 }}
        />
    )

    if (loading) {
        return (
            <Grid item xs={12}>
                <Spiner/>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Grid>
        )
    }

    if (!params?.orderItemRefundId) {
        return (
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>환불 요청 상세</Title>
                    {renderDetailErrorState()}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            </Grid>
        )
    }

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>환불 요청 상세</Title>
                <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }} >
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item xs={12}>
                            <RefundImagePreview imageSrc={mainImage} />
                        </Grid>

                        <Grid item xs={12} sm={12} md={3}>
                            <ListItem>
                                <ListItemText primary="기업명" secondary={params.gifticon_order_item.gifticon_order.company.companyName || ''} />
                            </ListItem>
                        </Grid>
                        <Grid item xs={12} sm={12} md={3}>
                            <ListItem>
                                <ListItemText primary="기프티콘" secondary={'[' + params.gifticon_order_item.gifticon_product.brandName + '] ' + params.gifticon_order_item.gifticon_product.goodsName} />
                            </ListItem>
                        </Grid>
                        <Grid item xs={12} sm={12} md={3}>
                            <ListItem>
                                <ListItemText primary="금액" secondary={formatNumber(params.refundAmount)} />
                            </ListItem>
                        </Grid>
                        <Grid item xs={12} sm={12} md={3}>
                            <ListItem>
                                <ListItemText primary="요청일" secondary={params.regTime || ''} />
                            </ListItem>
                        </Grid>
                        <Grid item xs={12} sm={12} md={3}>
                            <ListItem>
                                <ListItemText primary="은행명" secondary={params.refundBank || ''} />
                            </ListItem>
                        </Grid>
                        <Grid item xs={12} sm={12} md={3}>
                            <ListItem>
                                <ListItemText primary="계좌번호" secondary={params.refundAccount || ''} />
                            </ListItem>
                        </Grid>
                        <Grid item xs={12} sm={12} md={3}>
                            <ListItem>
                                <ListItemText primary="계좌주명" secondary={params.refundName || ''} />
                            </ListItem>
                        </Grid>
                        <Grid item xs={12} sm={12} md={3}>
                            <ListItem>
                                <ListItemText primary="환불일" secondary={params.refundTime || ''} />
                            </ListItem>
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <ListItem>
                                <ListItemText primary="환불사유" secondary={params.refundReason || ''} />
                            </ListItem>
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item xs={12} sm={12} md={3}>
                            <FormControl fullWidth>
                                <TextField
                                    required
                                    select
                                    label="환불상태"
                                    id="status"
                                    name="status"
                                    value={params.status}
                                    size="small"
                                    sx={{ minWidth: 150 }}
                                    onChange={handleUpdateField}
                                    disabled={saving}
                                >
                                    <MenuItem value={0}>환불요청</MenuItem>
                                    <MenuItem value={1}>환불완료</MenuItem>
                                </TextField>
                            </FormControl>
                        </Grid>
                    </Grid>
                    <BottomButtonGroup
                        visible={true}
                        submitDisabled={saving}
                        submitLabel={saving ? '저장 중...' : '저장'}
                        backDisabled={saving}
                        onBackClick={() => moveToList()}
                    />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
